import React, { useState, useCallback, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position, useReactFlow, useNodes } from 'reactflow';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { ImPowerCord } from "react-icons/im";
import { FiEdit2 } from 'react-icons/fi';
import './ConsumptionNode.css'; // Import the CSS file
import ConsumptionConfigModal from './ConsumptionConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { notifyError } from '../../../errors/notifyError';

const ConsumptionNode = ({ id, data, selected }) => {
  // Extract data properties, ensuring we handle both direct properties and nested ones
  const { name, fileName, file_name, inputMode, year, preview, bankingPolicyName, balanceMode, units } = data;
  const existingOaReUnits = data.existingOaReUnits ?? data.existing_oa_re_units ?? data.node_params?.[0]?.existing_oa_re_units ?? 0;
  const existingBtmUnits = data.existingBtmUnits ?? data.existing_btm_units ?? data.node_params?.[0]?.existing_btm_units ?? 0;
  const existingOaType = data.existingOaType ?? data.existing_oa_type ?? data.node_params?.[0]?.existing_oa_type ?? 'solar';
  const existingOaInputMode = data.existingOaInputMode ?? data.existing_oa_input_mode ?? data.node_params?.[0]?.existing_oa_input_mode ?? 'annual';
  const existingOaFileName = data.existingOaFileName ?? data.existing_oa_hourly_filename ?? data.node_params?.[0]?.existing_oa_hourly_filename ?? '';
  // Use fileName or file_name (from node_params) if available
  const displayFileName = fileName || file_name || (data.node_params && data.node_params[0] && data.node_params[0].file_name);
  const displayMode = inputMode || data.consumption_input_mode || (data.node_params && data.node_params[0] && data.node_params[0].consumption_input_mode) || 'hourly_csv';
  const displayYear = year || data.consumption_year || (data.node_params && data.node_params[0] && data.node_params[0].consumption_year);
  const displayPreview = preview || data.consumption_preview || (data.node_params && data.node_params[0] && data.node_params[0].consumption_preview);
  const displayBankingPolicy = bankingPolicyName || data.consumption_banking_policy || (data.node_params && data.node_params[0] && data.node_params[0].consumption_banking_policy) || displayPreview?.bankingPolicyName;
  const displayBalanceMode = balanceMode || data.balanceMode || data.balance_mode || (data.node_params && data.node_params[0] && (data.node_params[0].balanceMode || data.node_params[0].balance_mode)) || 'calculated';
  const displaySlotMonthlyData = data.slotMonthlyData || data.slot_monthly_data || (data.node_params && data.node_params[0] && data.node_params[0].slot_monthly_data) || '';
  const displayUnits = units || data.units || (data.node_params && data.node_params[0] && data.node_params[0].units) || displayPreview?.units || [];
  const displayUnitCount = displayMode === 'multi_unit_slot_monthly'
    ? (displayPreview?.unitCount || displayUnits.length || 0)
    : 0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const nodes = useNodes();
  const { session, addConsumptionNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`ConsumptionNode ${id} received data:`, data);
  }, [id, data]);

  // Check if node is configured properly
  const isConfigured = data.isConfigured || data.isApiConfigured || !!displayFileName;
  const canEditConsumption = isConfigured && (
    displayMode === 'multi_unit_slot_monthly'
    || displayMode === 'hourly_csv'
    || displayMode === 'slot_monthly'
  );
  const editLabel = displayMode === 'multi_unit_slot_monthly' ? 'Edit consumption units' : 'Edit consumption data';

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback(() => {
    if (isConfigured && !canEditConsumption) {
      return;
    }
    
    setIsModalOpen(true);
  }, [isConfigured, canEditConsumption]);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const {
        name, file, mode, year, preview, bankingPolicyName, balanceMode, units, slotMonthlyData, retainedFileName,
        existingOaReUnits, existingBtmUnits, existingOaType, existingOaInputMode, existingOaFileName,
      } = configData;
      const position = getNodePosition();
      const configuredFileName = file?.name
        || retainedFileName
        || (mode === 'multi_unit_slot_monthly' ? 'Multi-unit monthly totals' : (mode === 'slot_monthly' ? 'Monthly slot totals' : ''));
      
      // Log the configuration data for debugging
      console.log(`Configuring consumption node ${id} -> ${name}:`, {
        configData,
        position
      });
      
      // First update the node ID to name mapping before adding to session
      updateNodeMapping(id, name);
      console.log(`Updated node mapping: ${id} -> ${name}`);
      
      // Add the consumption node to the session state
      addConsumptionNode(
        name,
        configuredFileName,
        file,
        { x: position.x, y: position.y },
        { inputMode: mode, year, preview, bankingPolicyName, balanceMode, units, slotMonthlyData, existingOaReUnits, existingBtmUnits, existingOaType, existingOaInputMode, existingOaFileName }
      );
      
      // Update the node data with the new configuration
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                name,
                fileName: configuredFileName,
                inputMode: mode,
                year,
                preview,
                bankingPolicyName,
                balanceMode,
                units,
                slotMonthlyData,
                existingOaReUnits,
                existingBtmUnits,
                existingOaType,
                existingOaInputMode,
                existingOaFileName,
                isApiConfigured: true
              }
            };
          }
          return node;
        })
      );
      
      // If the node was previously selected, update the selection with the new name
      if (session.selected_node === id) {
        console.log(`Node was selected, updating selection from ${id} to ${name}`);
        updateSelectedNode(name);
      }
    } catch (error) {
      console.error('Error configuring consumption node:', error);
      notifyError(error);
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [id, reactFlowInstance, getNodePosition, closeConfigModal, updateNodeMapping, addConsumptionNode, session, updateSelectedNode]);

  return (
    <>
      <div
        className={`consumption-node ${selected ? 'consumption-selected' : isConfigured ? 'consumption-configured' : 'consumption-not-configured'}`}
      >
        <div className={`consumption-node-header ${isConfigured ? 'consumption-configured' : 'consumption-not-configured'}`}>
          <div className="consumption-node-header-title">
            <div className="consumption-node-type">Consumption</div>
            {name && isConfigured && <div className="consumption-node-name-display">{name}</div>}
          </div>
          <div className="consumption-node-header-icons">
            {(!isConfigured || canEditConsumption) && (
              <button 
                onClick={openConfigModal}
                className="consumption-config-button"
                title={canEditConsumption ? editLabel : 'Configure Consumption Node'}
                aria-label={canEditConsumption ? editLabel : 'Configure Consumption Node'}
                disabled={isConfiguring}
              >
                {canEditConsumption
                  ? <FiEdit2 className="consumption-icon" />
                  : <ExclamationTriangleIcon className="consumption-icon" />}
              </button>
            )}
            <ImPowerCord className="consumption-icon" />
          </div>
        </div>
        <div className="consumption-node-content">
          <div className="consumption-text-xs">
            {!isConfigured && (
              <div className="consumption-text-warning">
                Parameters not configured
              </div>
            )}
            {isConfiguring && (
              <div className="consumption-text-info">
                Configuring...
              </div>
            )}
            {isConfigured && (
              <>
                <div>File: {displayFileName || 'N/A'}</div>
                {Number(existingOaReUnits) > 0 || existingOaInputMode === 'hourly_csv' ? (
                  <div>
                    Existing OA: {existingOaType}
                    {existingOaInputMode === 'hourly_csv' ? ' hourly CSV' : ' annual'}
                  </div>
                ) : null}
                {displayMode === 'multi_unit_slot_monthly' && (
                  <div className="consumption-preview-summary">
                    <div>{displayUnitCount} units</div>
                    <div>Year: {displayYear || 'N/A'}</div>
                    <div>Policy: {displayBankingPolicy || 'N/A'}</div>
                  </div>
                )}
                {displayMode === 'slot_monthly' && (
                  <div className="consumption-preview-summary">
                    <div>Year: {displayYear || 'N/A'}</div>
                    <div>Policy: {displayBankingPolicy || 'N/A'}</div>
                    {displayPreview?.monthTotals && (
                      <div>
                        Annual total: {formatNumber(Object.values(displayPreview.monthTotals).reduce((sum, value) => sum + Number(value || 0), 0))}
                      </div>
                    )}
                    {displayPreview && (
                      <button
                        type="button"
                        className="consumption-preview-button"
                        onClick={() => setIsPreviewOpen(true)}
                      >
                        View calculated data
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="consumption-flex-container">
            {/* Only output handle, no input handle */}
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`consumption-connection-point ${isConfigured ? 'consumption-configured-output' : 'consumption-not-configured'}`}
              isConnectable={isConfigured} // Only allow connections if the node is configured
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
          </div>
        </div>
      </div>

      <ConsumptionConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        initialData={{ 
          name,
          position: getNodePosition(),
          mode: displayMode,
          fileName: displayFileName,
          year: displayYear,
          bankingPolicyName: displayBankingPolicy,
          balanceMode: displayBalanceMode,
          slotMonthlyData: displaySlotMonthlyData,
          units: displayUnits,
          existingOaReUnits,
          existingBtmUnits,
          existingOaType,
          existingOaInputMode,
          existingOaFileName,
          isEditing: canEditConsumption,
        }}
      />
      {isPreviewOpen && (
        createPortal(
          <ConsumptionPreviewDialog
            preview={displayPreview}
            onClose={() => setIsPreviewOpen(false)}
          />,
          document.body
        )
      )}
    </>
  );
};

const formatNumber = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const ConsumptionPreviewDialog = ({ preview, onClose }) => {
  if (!preview) return null;
  const monthLabels = preview.monthLabels || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthKeys = preview.months || monthLabels.map((_, idx) => String(idx + 1));
  const slots = preview.slots || Object.keys(preview.monthlySlotTotals || {});
  const slotDetailsByName = (preview.slotDetails || []).reduce((acc, detail) => {
    if (detail?.name) acc[detail.name] = detail;
    return acc;
  }, {});
  const hourlyRows = preview.hourlyByMonth || [];

  return (
    <div className="consumption-preview-overlay" role="dialog" aria-modal="true">
      <div className="consumption-preview-dialog">
        <div className="consumption-preview-header">
          <div>
            <div className="consumption-preview-title">Calculated Consumption</div>
            <div className="consumption-preview-subtitle">Year {preview.year}</div>
          </div>
          <button type="button" onClick={onClose} className="consumption-preview-close">Close</button>
        </div>

        <div className="consumption-preview-body">
          <div className="consumption-preview-section-title">Monthly Slot Totals</div>
          <div className="consumption-preview-table-wrap">
            <table className="consumption-preview-table">
              <thead>
                <tr>
                  <th>Slot</th>
                  {monthLabels.map((label) => <th key={label}>{label}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr className="consumption-preview-total-row">
                  <td>Total units</td>
                  {monthKeys.map((key) => <td key={key}>{formatNumber(preview.monthTotals?.[key])}</td>)}
                </tr>
                {slots.map((slot) => (
                  <tr key={slot}>
                    <td>
                      <div>{slotDetailsByName[slot]?.displayName || slot}</div>
                      {!!slotDetailsByName[slot]?.timeBlocks?.length && (
                        <div className="consumption-preview-slot-time-blocks">
                          {slotDetailsByName[slot].timeBlocks.join(', ')}
                        </div>
                      )}
                    </td>
                    {monthKeys.map((key) => <td key={`${slot}-${key}`}>{formatNumber(preview.monthlySlotTotals?.[slot]?.[key])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="consumption-preview-section-title">Hourly Values</div>
          <div className="consumption-preview-table-wrap">
            <table className="consumption-preview-table">
              <thead>
                <tr>
                  <th>Time</th>
                  {monthLabels.map((label) => <th key={label}>{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {hourlyRows.map((row) => (
                  <tr key={row.Time}>
                    <td>{row.Time}</td>
                    {monthKeys.map((key) => <td key={`${row.Time}-${key}`}>{formatNumber(row[key])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsumptionNode;
